import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { CompanyListRow } from 'types'
import { useMemo } from 'react'
import { stripPlanName } from 'utils/planName'
import { COMPANY_SEGMENTS, type CompanySegment } from '@repo/types'

// VRG-706: `data` is the list ROW, `{ company, plan, counts }`, not `Company[]`. Typed as
// companies, `row.id` compiled and was undefined at runtime: the tracked-link picker listed
// blank companies and posted `company_id: "undefined"`. Unwrap with `listedCompanies()`.
export interface CompaniesResponse {
  data: CompanyListRow[]
  total: number
  limit: number
  offset: number
}

type UseCompaniesParams = {
  limit?: number
  offset?: number
  sortField?: string
  sortDir?: 'asc' | 'desc'
  q?: string
  showArchived?: boolean
  includeDemo?: boolean
  /**
   * VRG-718 — Accounts → Companies' segment chips. When set it REPLACES showArchived /
   * includeDemo on the server; leave it unset everywhere else (the pickers' default excludes
   * demos and archived). An empty array means every segment.
   */
  segments?: readonly CompanySegment[]
  /** VRG-718 — has a billable activation (true) / has none (false). */
  activated?: boolean
  /** VRG-718 — inclusive bounds on the billable activation count. */
  activationsMin?: number
  activationsMax?: number
}

type UseCompaniesResult = {
  data: CompaniesResponse | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export const useCompaniesQuery = (params: UseCompaniesParams = {}, options?: { enabled?: boolean }): UseCompaniesResult => {
  const stableParams = useMemo(() => {
    const base: Record<string, any> = {
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }
    if (params.sortField && params.sortDir) {
      base.sort = `${params.sortField}:${params.sortDir}`
    }
    if (params.q) {
      base.q = params.q
    }
    if (params.showArchived) {
      base.showArchived = true
    }
    if (params.includeDemo) {
      base.includeDemo = true
    }
    if (params.segments) {
      // Empty = every segment; sent explicitly so the server does not fall back to the
      // pickers' exclusive default.
      base.segments = (params.segments.length ? params.segments : COMPANY_SEGMENTS).join(',')
    }
    if (params.activated !== undefined) base.activated = params.activated
    if (params.activationsMin !== undefined) base.activationsMin = params.activationsMin
    if (params.activationsMax !== undefined) base.activationsMax = params.activationsMax
    return base
  }, [
    params.limit,
    params.offset,
    params.sortField,
    params.sortDir,
    params.q,
    params.showArchived,
    params.includeDemo,
    params.segments?.join(','),
    params.activated,
    params.activationsMin,
    params.activationsMax,
  ])

  const isEnabled = options?.enabled !== false

  const query = useQuery({
    queryKey: ['companies', 'list', stableParams],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/companies/admin',
        params: stableParams,
      })
      // Strip the "Vera " plan prefix at the boundary, same as `subscriptionApi` does
      // for the catalog — this endpoint carries its own plan payload (`plan.name`), so
      // without this the internal Companies list would keep showing "Vera Business"
      // while every customer-facing surface shows "Business". See utils/planName.
      //
      // The rows are on `.data`, NOT `.companies` (see CompaniesResponse above). Mapping
      // the wrong key here is silent: it returns an object with an extra undefined key,
      // the real rows pass through unstripped, and nothing errors.
      const payload = response.data as CompaniesResponse
      return {
        ...payload,
        data: Array.isArray(payload?.data)
          ? payload.data.map((row) => (row?.plan ? { ...row, plan: stripPlanName(row.plan) } : row))
          : payload?.data,
      } as CompaniesResponse
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 10, // 10 minutes
  })

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
